import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useVirtualizer } from '@tanstack/react-virtual';
import { motion, AnimatePresence } from 'framer-motion';
import { AlertCircle, Ban, ChevronDown, ChevronRight, Globe, Search, Server, Shield } from 'lucide-react';
import { PageHeader } from '../components/ui/PageHeader';
import { StatTile } from '../components/ui/StatTile';
import { EmptyState } from '../components/ui/EmptyState';
import { SkeletonLoader } from '../components/SkeletonLoader';
import { networkDataCacheService, type NetworkDataCacheResult } from '../services/networkDataCache';
import { configService } from '../services/usersApi';
import { ItemBan, ParsedBanTarget } from '../types/networkData';
import { parseBanTarget } from '../utils/networkData';
import { ItemIcon } from '../components/ItemIcon';
import { Highlight } from '../components/Highlight';
import { RedisKeyInfo } from '../components/RedisKeyInfo';
import { CacheStatusIndicator } from '../components/CacheStatusIndicator';
import { useDocumentTitle } from '../hooks/useDocumentTitle';
import { useIsMobile } from '../hooks/useIsMobile';
import { cn } from '../utils/cn';

// ---------------------------------------------------------------------------
// Helper types
// ---------------------------------------------------------------------------

interface EnhancedBan extends ItemBan {
  parsed: ParsedBanTarget;
}

interface BansByScope {
  networkWide: EnhancedBan[];
  byCluster: Record<string, EnhancedBan[]>;
  bySingle: Record<string, EnhancedBan[]>;
}

type ScopeFilter = 'all' | 'network' | 'cluster' | 'single';

// ---------------------------------------------------------------------------
// Scope badge
// ---------------------------------------------------------------------------

const ScopeBadge = ({ parsed }: { parsed: ParsedBanTarget }) => {
  switch (parsed.scope) {
    case 'all':
      return (
        <span className="inline-flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded-full bg-red-500/15 text-red-300 whitespace-nowrap">
          <Globe className="w-3 h-3 flex-shrink-0" />
          Network-wide
        </span>
      );
    case 'cluster':
      return (
        <span className="inline-flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded-full bg-accent-500/15 text-accent-300 whitespace-nowrap max-w-[160px]">
          <Shield className="w-3 h-3 flex-shrink-0" />
          <span className="truncate">{parsed.value}</span>
        </span>
      );
    case 'single':
      return (
        <span className="inline-flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded-full bg-amber-500/15 text-amber-300 whitespace-nowrap max-w-[160px]">
          <Server className="w-3 h-3 flex-shrink-0" />
          <span className="truncate">{parsed.value}</span>
        </span>
      );
  }
};

// ---------------------------------------------------------------------------
// Item ban row (used in flat search results)
// ---------------------------------------------------------------------------

const BanRow = ({
  ban,
  query,
  clusterIdFallback,
  isMobile,
}: {
  ban: EnhancedBan;
  query: string;
  clusterIdFallback: string;
  isMobile: boolean;
}) => {
  const clusterId =
    ban.parsed.scope === 'cluster' && ban.parsed.value
      ? ban.parsed.value
      : clusterIdFallback;

  if (isMobile) {
    return (
      <div className="px-3 py-3 border-b border-glass-200/10 hover:bg-glass-200/5 transition-colors">
        <div className="flex items-center gap-3">
          <div className="flex-shrink-0 w-8 h-8 flex items-center justify-center overflow-hidden rounded">
            <ItemIcon
              clusterId={clusterId}
              itemId={ban.id}
              initialDisplayName={ban.displayName}
              displayMode="inline"
              className="w-7 h-7 object-contain flex-shrink-0"
            />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-sm font-medium text-white truncate">
              <Highlight text={ban.displayName} highlight={query} />
            </div>
            <div className="text-xs text-glass-500 font-mono truncate">
              <Highlight text={ban.id} highlight={query} />
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2 mt-2 pl-11 flex-wrap">
          <span className="text-xs px-2 py-0.5 rounded bg-glass-200/10 text-glass-300 truncate max-w-[60%]">
            <Highlight text={ban.reason} highlight={query} />
          </span>
          <ScopeBadge parsed={ban.parsed} />
        </div>
      </div>
    );
  }

  return (
    <div className="flex items-center gap-4 px-4 py-3 border-b border-glass-200/10 hover:bg-glass-200/5 transition-colors">
      <div className="flex-shrink-0 w-8 h-8 flex items-center justify-center overflow-hidden rounded">
        <ItemIcon
          clusterId={clusterId}
          itemId={ban.id}
          initialDisplayName={ban.displayName}
          displayMode="inline"
          className="w-7 h-7 object-contain flex-shrink-0"
        />
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-sm font-medium text-white truncate">
          <Highlight text={ban.displayName} highlight={query} />
        </div>
        <div className="text-xs text-glass-500 font-mono truncate">
          <Highlight text={ban.id} highlight={query} />
        </div>
      </div>
      <div className="flex-shrink-0 max-w-[200px]">
        <span className="text-xs px-2 py-0.5 rounded bg-glass-200/10 text-glass-300 block truncate">
          <Highlight text={ban.reason} highlight={query} />
        </span>
      </div>
      <div className="flex-shrink-0">
        <ScopeBadge parsed={ban.parsed} />
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Grouped ban row (inside a cluster/scope section)
// Uses ItemIcon fully to show icon + display name + hover-to-reveal ID
// ---------------------------------------------------------------------------

const GroupedBanRow = ({
  ban,
  clusterId,
  isMobile,
}: {
  ban: EnhancedBan;
  clusterId: string;
  isMobile: boolean;
}) => {
  return (
    <div className={cn(
      'flex items-center gap-3 border-b border-glass-200/10 hover:bg-glass-200/5 transition-colors',
      isMobile ? 'px-3 py-2' : 'px-4 py-2.5'
    )}>
      <div className="flex-shrink-0 w-7 h-7 flex items-center justify-center overflow-hidden rounded">
        <ItemIcon
          clusterId={clusterId}
          itemId={ban.id}
          initialDisplayName={ban.displayName}
          displayMode="inline"
          className="w-6 h-6 object-contain flex-shrink-0"
        />
      </div>
      <div className="flex-1 min-w-0">
        <span className="font-medium text-white text-sm truncate block">
          {ban.displayName}
        </span>
        <span className="text-xs text-glass-500 font-mono truncate block">{ban.id}</span>
      </div>
      <span className={cn(
        'text-xs px-2 py-0.5 rounded bg-glass-200/10 text-glass-300 flex-shrink-0 truncate',
        isMobile ? 'max-w-[35%]' : 'max-w-[200px]'
      )}>
        {ban.reason}
      </span>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Cluster section (collapsible)
// ---------------------------------------------------------------------------

const ClusterSection = ({
  clusterId,
  bans,
  thumbnailUrl,
  fallbackClusterId,
  isMobile,
}: {
  clusterId: string;
  bans: EnhancedBan[];
  thumbnailUrl?: string;
  fallbackClusterId: string;
  isMobile: boolean;
}) => {
  const [isExpanded, setIsExpanded] = useState(false);

  return (
    <div className="glass-card overflow-hidden">
      <button
        onClick={() => setIsExpanded((prev) => !prev)}
        className="w-full p-3 md:p-4 flex items-center justify-between hover:bg-glass-200/5 transition-colors"
      >
        <div className="flex items-center gap-3 min-w-0 flex-1">
          {thumbnailUrl && (
            <img
              src={thumbnailUrl}
              alt={clusterId}
              className="w-8 h-8 md:w-10 md:h-10 rounded-md object-cover flex-shrink-0"
            />
          )}
          <div className="text-left min-w-0">
            <h3 className="text-sm md:text-base font-semibold text-white truncate">{clusterId}</h3>
            <p className="text-xs text-glass-400">
              {bans.length} banned {bans.length === 1 ? 'item' : 'items'}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2 md:gap-3 flex-shrink-0 ml-2">
          <span className="text-xs md:text-sm font-semibold bg-accent-500/15 text-accent-300 px-2 md:px-3 py-1 rounded-full">
            {bans.length}
          </span>
          {isExpanded ? (
            <ChevronDown className="w-4 h-4 md:w-5 md:h-5 text-glass-300" />
          ) : (
            <ChevronRight className="w-4 h-4 md:w-5 md:h-5 text-glass-300" />
          )}
        </div>
      </button>
      <AnimatePresence>
        {isExpanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="overflow-hidden border-t border-glass-200/10"
          >
            <div className="max-h-[50vh] overflow-y-auto">
              {bans.map((ban) => (
                <GroupedBanRow
                  key={`${ban.id}-${ban.target}`}
                  ban={ban}
                  isMobile={isMobile}
                  clusterId={
                    ban.parsed.scope === 'cluster' && ban.parsed.value
                      ? ban.parsed.value
                      : fallbackClusterId
                  }
                />
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Virtualized flat search results
// ---------------------------------------------------------------------------

const VirtualizedBanList = ({
  bans,
  query,
  clusterIdFallback,
  isMobile,
}: {
  bans: EnhancedBan[];
  query: string;
  clusterIdFallback: string;
  isMobile: boolean;
}) => {
  const parentRef = useRef<HTMLDivElement>(null);

  const virtualizer = useVirtualizer({
    count: bans.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => (isMobile ? 88 : 64),
    overscan: 15,
  });

  return (
    <div ref={parentRef} className="glass-card overflow-y-auto max-h-[70vh]">
      <div
        style={{
          height: `${virtualizer.getTotalSize()}px`,
          width: '100%',
          position: 'relative',
        }}
      >
        {virtualizer.getVirtualItems().map((virtualRow) => {
          const ban = bans[virtualRow.index];
          return (
            <div
              key={`${ban.id}-${ban.target}`}
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: `${virtualRow.size}px`,
                transform: `translateY(${virtualRow.start}px)`,
              }}
            >
              <BanRow
                ban={ban}
                query={query}
                clusterIdFallback={clusterIdFallback}
                isMobile={isMobile}
              />
            </div>
          );
        })}
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Main view
// ---------------------------------------------------------------------------

export function ItemBansView() {
  useDocumentTitle('Item Bans');
  const isMobile = useIsMobile();

  const [itemBans, setItemBans] = useState<EnhancedBan[]>([]);
  const [redisKey, setRedisKey] = useState<string | null>(null);
  const [cachedAt, setCachedAt] = useState<number | null>(null);
  const [clusterThumbnails, setClusterThumbnails] = useState<Record<string, string>>({});
  const [fqdnToCluster, setFqdnToCluster] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [scopeFilter, setScopeFilter] = useState<ScopeFilter>('all');

  // Fetch data
  const fetchData = useCallback(async (forceRefresh = false) => {
    try {
      setLoading(true);
      setError(null);

      // Cached copy first (if any), then the fresh document when the background re-read lands.
      const applyNetwork = (networkResult: NetworkDataCacheResult) => {
        setRedisKey(networkResult.key);
        setCachedAt(networkResult.cachedAt);

        if (networkResult.data?.itemBans) {
          const enhanced: EnhancedBan[] = networkResult.data.itemBans.map((ban) => ({
            ...ban,
            parsed: parseBanTarget(ban.target),
          }));
          setItemBans(enhanced);
        } else {
          setItemBans([]);
        }
      };

      const [networkResult, serverConfigs, clusterMapping] = await Promise.all([
        networkDataCacheService.get(forceRefresh, applyNetwork),
        configService.getConfigs(),
        configService.getClusterFqdnMapping(),
      ]);

      if (serverConfigs) {
        const thumbnails = serverConfigs.reduce((acc, config) => {
          if (config.cluster_name && config.thumbnail_url) {
            acc[config.cluster_name] = config.thumbnail_url;
          }
          return acc;
        }, {} as Record<string, string>);
        setClusterThumbnails(thumbnails);
      }

      if (clusterMapping) {
        const map: Record<string, string> = {};
        for (const cluster in clusterMapping) {
          for (const fqdn of clusterMapping[cluster]) {
            map[fqdn] = cluster;
          }
        }
        setFqdnToCluster(map);
      }

      applyNetwork(networkResult);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch network data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleRefresh = useCallback(async () => {
    await fetchData(true);
  }, [fetchData]);

  const fallbackClusterId = useMemo(() => {
    const clusterIds = Object.keys(clusterThumbnails);
    return clusterIds.length > 0 ? clusterIds[0] : 'UNKNOWN';
  }, [clusterThumbnails]);

  const grouped: BansByScope = useMemo(() => {
    const result: BansByScope = {
      networkWide: [],
      byCluster: {},
      bySingle: {},
    };

    for (const ban of itemBans) {
      switch (ban.parsed.scope) {
        case 'all':
          result.networkWide.push(ban);
          break;
        case 'cluster':
          if (ban.parsed.value) {
            if (!result.byCluster[ban.parsed.value]) {
              result.byCluster[ban.parsed.value] = [];
            }
            result.byCluster[ban.parsed.value].push(ban);
          }
          break;
        case 'single':
          if (ban.parsed.value) {
            if (!result.bySingle[ban.parsed.value]) {
              result.bySingle[ban.parsed.value] = [];
            }
            result.bySingle[ban.parsed.value].push(ban);
          }
          break;
      }
    }

    return result;
  }, [itemBans]);

  const filteredBans = useMemo(() => {
    let list = itemBans;

    if (scopeFilter !== 'all') {
      list = list.filter((ban) => {
        switch (scopeFilter) {
          case 'network':
            return ban.parsed.scope === 'all';
          case 'cluster':
            return ban.parsed.scope === 'cluster';
          case 'single':
            return ban.parsed.scope === 'single';
          default:
            return true;
        }
      });
    }

    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      list = list.filter((ban) => {
        return (
          ban.id.toLowerCase().includes(q) ||
          ban.displayName.toLowerCase().includes(q) ||
          ban.reason.toLowerCase().includes(q) ||
          ban.target.toLowerCase().includes(q) ||
          (ban.parsed.value && ban.parsed.value.toLowerCase().includes(q))
        );
      });
    }

    return list;
  }, [itemBans, scopeFilter, searchQuery]);

  const stats = useMemo(() => {
    return {
      total: itemBans.length,
      networkWide: grouped.networkWide.length,
      clusters: Object.keys(grouped.byCluster).length,
      clusterBans: Object.values(grouped.byCluster).reduce((sum, arr) => sum + arr.length, 0),
      servers: Object.keys(grouped.bySingle).length,
      serverBans: Object.values(grouped.bySingle).reduce((sum, arr) => sum + arr.length, 0),
    };
  }, [itemBans, grouped]);

  const sortedClusterIds = useMemo(
    () => Object.keys(grouped.byCluster).sort(),
    [grouped]
  );

  const sortedSingleIds = useMemo(
    () => Object.keys(grouped.bySingle).sort(),
    [grouped]
  );

  const isSearching = searchQuery.trim().length > 0 || scopeFilter !== 'all';

  const scopeFilters: { key: ScopeFilter; label: string; count: number }[] = [
    { key: 'all', label: 'All Scopes', count: stats.total },
    { key: 'network', label: 'Network-wide', count: stats.networkWide },
    { key: 'cluster', label: 'Cluster', count: stats.clusterBans },
    { key: 'single', label: 'Server', count: stats.serverBans },
  ];

  if (loading) {
    return (
      <div className="space-y-6">
        <PageHeader eyebrow="Network Config" title="Banned Items" description="Loading banned item registry..." icon={Ban} />
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {Array.from({ length: 4 }).map((_, i) => <SkeletonLoader key={i} className="h-20" />)}
        </div>
        <div className="glass-card p-4 space-y-3">
          {Array.from({ length: 8 }).map((_, i) => <SkeletonLoader key={i} className="h-9" />)}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-6">
        <PageHeader eyebrow="Network Config" title="Banned Items" icon={Ban} />
        <EmptyState tone="danger" icon={AlertCircle} title="Failed to load banned items" description={error} />
      </div>
    );
  }

  return (
    <div className="space-y-4 md:space-y-6">
      <PageHeader
        eyebrow="Network Config"
        title="Banned Items"
        description="Items prohibited from use across the network, grouped by scope."
        icon={Ban}
        actions={
          <>
            <CacheStatusIndicator cachedAt={cachedAt} onRefresh={handleRefresh} />
            <RedisKeyInfo redisKey={redisKey} />
          </>
        }
      />

      {/* Stats summary */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
        <StatTile label={<>Total Bans</>} value={stats.total.toLocaleString()} tone="neutral" />
        <StatTile label={<>Network-wide</>} value={stats.networkWide} tone="danger" />
        <StatTile label={<>Clusters ({stats.clusterBans})</>} value={stats.clusters} tone="accent" />
        <StatTile label={<>Servers ({stats.serverBans})</>} value={stats.servers} tone="amber" />
      </div>

      {/* Search + filter bar */}
      <div className="flex flex-col gap-3">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-glass-400" />
          <input
            type="text"
            placeholder="Search by item name, ID, reason, scope..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="glass-input w-full pl-10"
          />
        </div>
        <div className="flex gap-1 flex-wrap">
          {scopeFilters.map((sf) => (
            <button
              key={sf.key}
              onClick={() => setScopeFilter(sf.key)}
              className={cn(
                'px-2.5 md:px-3 py-1.5 md:py-2 rounded-lg text-xs md:text-sm font-medium transition-colors whitespace-nowrap',
                scopeFilter === sf.key
                  ? 'bg-accent-500/15 text-accent-300 border border-accent-400/30'
                  : 'text-glass-300 hover:bg-glass-200/10 hover:text-white border border-transparent'
              )}
            >
              {sf.label}
              <span className="ml-1 text-xs opacity-60">({sf.count})</span>
            </button>
          ))}
        </div>
      </div>

      {/* Content */}
      {isSearching ? (
        <div className="space-y-2">
          <p className="text-sm text-glass-300">
            {filteredBans.length.toLocaleString()} result{filteredBans.length !== 1 ? 's' : ''}
          </p>
          {filteredBans.length === 0 ? (
            <div className="glass-card p-8 text-center text-glass-400">
              No banned items match your search.
            </div>
          ) : (
            <VirtualizedBanList
              bans={filteredBans}
              query={searchQuery}
              clusterIdFallback={fallbackClusterId}
              isMobile={isMobile}
            />
          )}
        </div>
      ) : (
        <div className="space-y-4 md:space-y-6">
          {/* Network-wide bans */}
          {grouped.networkWide.length > 0 && (
            <div className="space-y-2">
              <h2 className="text-base md:text-sm font-semibold text-white flex items-center gap-2">
                <Globe className="w-4 h-4 md:w-5 md:h-5 text-red-400 flex-shrink-0" />
                Network-wide Bans
                <span className="text-xs md:text-sm font-normal text-glass-400">
                  ({grouped.networkWide.length})
                </span>
              </h2>
              <ClusterSection
                clusterId="Network-wide"
                bans={grouped.networkWide}
                fallbackClusterId={fallbackClusterId}
                isMobile={isMobile}
              />
            </div>
          )}

          {/* Cluster-scoped bans */}
          {sortedClusterIds.length > 0 && (
            <div className="space-y-2">
              <h2 className="text-base md:text-sm font-semibold text-white flex items-center gap-2">
                <Shield className="w-4 h-4 md:w-5 md:h-5 text-accent-400 flex-shrink-0" />
                Cluster Bans
                <span className="text-xs md:text-sm font-normal text-glass-400">
                  ({stats.clusterBans} across {stats.clusters})
                </span>
              </h2>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 md:gap-4">
                {sortedClusterIds.map((cid) => (
                  <ClusterSection
                    key={cid}
                    clusterId={cid}
                    bans={grouped.byCluster[cid]}
                    thumbnailUrl={clusterThumbnails[cid]}
                    fallbackClusterId={cid}
                    isMobile={isMobile}
                  />
                ))}
              </div>
            </div>
          )}

          {/* FQDN-scoped bans */}
          {sortedSingleIds.length > 0 && (
            <div className="space-y-2">
              <h2 className="text-base md:text-sm font-semibold text-white flex items-center gap-2">
                <Server className="w-4 h-4 md:w-5 md:h-5 text-amber-400 flex-shrink-0" />
                Server-specific Bans
                <span className="text-xs md:text-sm font-normal text-glass-400">
                  ({stats.serverBans} across {stats.servers})
                </span>
              </h2>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 md:gap-4">
                {sortedSingleIds.map((fqdn) => {
                  const clusterForFqdn = fqdnToCluster[fqdn] || fallbackClusterId;
                  return (
                    <ClusterSection
                      key={fqdn}
                      clusterId={fqdn}
                      bans={grouped.bySingle[fqdn]}
                      thumbnailUrl={
                        fqdnToCluster[fqdn]
                          ? clusterThumbnails[fqdnToCluster[fqdn]]
                          : undefined
                      }
                      fallbackClusterId={clusterForFqdn}
                      isMobile={isMobile}
                    />
                  );
                })}
              </div>
            </div>
          )}

          {itemBans.length === 0 && (
            <div className="glass-card p-8 text-center text-glass-400">
              No banned items found in the network configuration.
            </div>
          )}
        </div>
      )}
    </div>
  );
}
